import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Badge, Button, Dialog, Input, Select, Table } from '../components/ui';
import { ConfirmDialog, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import { TenantBrandingSchema, resolveAccentInk, type TenantRole } from '@kioskforge/shared';
import {
  changeRole,
  getTenant,
  inviteUser,
  listUsers,
  removeUser,
  updateTenant,
  type TenantRecord,
  type UserRecord
} from '../api/tenant';
import { ApiError } from '../api/client';

const TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney'
];

const ROLES: TenantRole[] = ['owner', 'admin', 'viewer'];

function messageOf(error: unknown): string {
  if (error instanceof ApiError && error.code === 'last_owner') return 'This is the last owner — promote another user first.';
  return error instanceof Error ? error.message : 'Something went wrong.';
}

export function Settings() {
  const { session } = useSession();
  const { notify } = useToast();
  const role = session?.role ?? 'viewer';
  const isOwner = role === 'owner';
  const isAdmin = role === 'owner' || role === 'admin';

  const [tenant, setTenant] = useState<TenantRecord | null>(null);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [retention, setRetention] = useState(90);
  const [accent, setAccent] = useState('');
  const [logo, setLogo] = useState('');
  const [brandName, setBrandName] = useState('');

  const [showInvite, setShowInvite] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<UserRecord | null>(null);

  const load = useCallback(async () => {
    if (!session) return;
    try {
      const tenantRecord = await getTenant(session.tenant.id);
      setTenant(tenantRecord);
      setName(tenantRecord.name ?? '');
      setTimezone(tenantRecord.timezone ?? 'UTC');
      setRetention(tenantRecord.retention_days ?? 90);
      setAccent(tenantRecord.branding?.accent ?? '');
      setLogo(tenantRecord.branding?.logo ?? '');
      setBrandName(tenantRecord.branding?.name ?? '');
      setUsers(await listUsers());
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load settings.');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const patch: Parameters<typeof updateTenant>[0] = {
        name: name.trim(),
        timezone,
        retention_days: Number(retention)
      };
      if (accent.trim()) {
        const branding = {
          accent: accent.trim(),
          accentInk: resolveAccentInk(accent.trim()),
          ...(logo.trim() ? { logo: logo.trim() } : {}),
          ...(brandName.trim() ? { name: brandName.trim() } : {})
        };
        // Reuse the shared contract: contrast + shape validated before sending.
        TenantBrandingSchema.parse(branding);
        patch.branding = branding;
      }
      await updateTenant(patch);
      notify('Settings saved');
      await load();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setSaving(false);
    }
  }

  async function onRoleChange(user: UserRecord, next: TenantRole) {
    try {
      await changeRole(user.id, next);
      notify(`${user.email ?? user.id} is now ${next}`);
      await load();
    } catch (cause) {
      setError(messageOf(cause));
    }
  }

  async function onRemove(user: UserRecord) {
    try {
      await removeUser(user.id);
      notify(`${user.email ?? user.id} removed`);
      await load();
    } catch (cause) {
      setError(messageOf(cause));
    }
  }

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / SETTINGS</span>
          <h1>Tenant settings</h1>
        </div>
      </header>

      {loading && <LoadingState label="Loading settings" />}
      {error && <ErrorState title="Settings unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && tenant && (
        <>
          <section className="panel">
            <header className="panel-heading">
              <span className="index-label">PROFILE &amp; BRANDING</span>
              <Badge>{tenant.status ?? 'active'}</Badge>
            </header>
            <div className="panel-body">
              <form onSubmit={saveProfile} className="settings-form">
                <label className="index-label" htmlFor="tenant-name">
                  BUSINESS NAME
                </label>
                <Input id="tenant-name" value={name} onChange={(event) => setName(event.target.value)} disabled={!isAdmin} />

                <label className="index-label" htmlFor="tenant-timezone">
                  TIMEZONE
                </label>
                <Input
                  id="tenant-timezone"
                  list="timezones"
                  value={timezone}
                  onChange={(event) => setTimezone(event.target.value)}
                  disabled={!isAdmin}
                />
                <datalist id="timezones">
                  {TIMEZONES.map((zone) => (
                    <option key={zone} value={zone} />
                  ))}
                </datalist>

                <label className="index-label" htmlFor="tenant-accent">
                  ACCENT COLOUR (HEX)
                </label>
                <div className="colour-row">
                  <Input
                    id="tenant-accent"
                    placeholder="#RRGGBB"
                    value={accent}
                    onChange={(event) => setAccent(event.target.value)}
                    disabled={!isAdmin}
                  />
                  <span className="colour-swatch" style={accent.trim() ? { backgroundColor: accent.trim() } : undefined} aria-hidden="true" />
                </div>
                <p className="detail-note">Used by player blocks. The text colour is chosen automatically to keep 4.5:1 contrast.</p>

                <label className="index-label" htmlFor="tenant-logo">
                  LOGO URL
                </label>
                <Input id="tenant-logo" value={logo} onChange={(event) => setLogo(event.target.value)} disabled={!isAdmin} />

                <label className="index-label" htmlFor="tenant-brand-name">
                  BRAND NAME (OPTIONAL)
                </label>
                <Input id="tenant-brand-name" value={brandName} onChange={(event) => setBrandName(event.target.value)} disabled={!isAdmin} />

                <div className="settings-actions">
                  <Button type="submit" disabled={!isAdmin || saving}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              </form>
            </div>
          </section>

          <section className="panel">
            <header className="panel-heading">
              <span className="index-label">DATA RETENTION</span>
            </header>
            <div className="panel-body">
              <label className="index-label" htmlFor="tenant-retention">
                KEEP SUBMISSIONS FOR (DAYS)
              </label>
              <Input
                id="tenant-retention"
                type="number"
                min={1}
                max={3650}
                value={retention}
                onChange={(event) => setRetention(Number(event.target.value))}
                disabled={!isAdmin}
              />
              <p className="detail-note">
                Visitor submissions (including names and any photos) are permanently deleted after this many days. Set the
                shortest period your operation needs.
              </p>
            </div>
          </section>

          <section className="panel">
            <header className="panel-heading">
              <span className="index-label">USERS &amp; ROLES</span>
              {isAdmin && (
                <Button variant="quiet" type="button" onClick={() => setShowInvite(true)}>
                  Invite user
                </Button>
              )}
            </header>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name ?? '—'}</td>
                    <td>{user.email ?? '—'}</td>
                    <td>
                      {isOwner ? (
                        <Select
                          aria-label={`Role for ${user.email}`}
                          value={user.role ?? 'viewer'}
                          onChange={(event) => void onRoleChange(user, event.target.value as TenantRole)}
                        >
                          {ROLES.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Badge>{user.role ?? 'viewer'}</Badge>
                      )}
                    </td>
                    <td>
                      {isOwner && (
                        <Button variant="quiet" type="button" onClick={() => setRemoveTarget(user)}>
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </section>
        </>
      )}

      <InviteDialog
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onInvited={() => {
          notify('Invite sent');
          void load();
        }}
      />

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Remove user"
        confirmWord="remove"
        detail={removeTarget ? `${removeTarget.email ?? removeTarget.id} loses access immediately.` : ''}
        confirmLabel="Remove"
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          const target = removeTarget;
          setRemoveTarget(null);
          if (target) void onRemove(target);
        }}
      />
    </main>
  );
}

function InviteDialog({ open, onClose, onInvited }: { open: boolean; onClose: () => void; onInvited: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('viewer');
  const [inviteName, setInviteName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('viewer');
      setInviteName('');
      setError('');
    }
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await inviteUser(email.trim(), role, inviteName.trim() || undefined);
      onInvited();
      onClose();
    } catch (cause) {
      setError(messageOf(cause));
    }
  }

  return (
    <Dialog open={open} title="Invite user" onClose={onClose}>
      {error && <ErrorState title="Could not invite" detail={error} />}
      <form onSubmit={submit} className="auth-form">
        <label className="index-label" htmlFor="invite-email">
          EMAIL
        </label>
        <Input id="invite-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
        <label className="index-label" htmlFor="invite-name">
          NAME
        </label>
        <Input id="invite-name" value={inviteName} onChange={(event) => setInviteName(event.target.value)} />
        <label className="index-label" htmlFor="invite-role">
          ROLE
        </label>
        <Select id="invite-role" value={role} onChange={(event) => setRole(event.target.value as TenantRole)}>
          {ROLES.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
        <p className="detail-note">The invitee receives a link to set their password and join this tenant.</p>
        <div className="dialog-actions">
          <Button variant="quiet" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Send invite</Button>
        </div>
      </form>
    </Dialog>
  );
}
